
import { EventFilters } from '@/lib/types';
import FilterSearch from './FilterSearch';
import { useFilterBar } from './useFilterBar';
import { ChevronLeft, Sparkles } from 'lucide-react';
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/contexts/AuthContext';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useIsMobile } from '@/hooks/use-mobile';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import MobileFilterSheet from './MobileFilterSheet';
import DesktopFilterContent from './DesktopFilterContent';

interface FilterBarProps {
  onFilterChange: (filters: EventFilters) => void;
  compact?: boolean;
  currentDate?: Date; // Prop to get the current date from Calendar
  className?: string;
  onClose?: () => void; // Optional onClose prop
  onCollapse?: () => void; // New prop for collapsing the filter sidebar
  isCollapsed?: boolean; // Whether the sidebar is already collapsed
}

const FilterBar = ({
  onFilterChange,
  compact = false,
  currentDate,
  className,
  onClose,
  onCollapse,
  isCollapsed = false
}: FilterBarProps) => {
  const [isInternallyCollapsed, setIsInternallyCollapsed] = useState(false);
  const isMobile = useIsMobile();

  const {
    user,
    isAuthenticated
  } = useAuth();

  const {
    search,
    setSearch,
    location,
    setLocation,
    selectedTypes,
    selectedProfessions,
    hasCreditPoints,
    toggleCreditPointsFilter,
    dateRange,
    setDateRange,
    handleTypeClick,
    handleProfessionClick,
    resetFilters,
    applyPreferenceFilter,
    hasActiveFilters
  } = useFilterBar(onFilterChange);

  // Handle collapsing - use either internal state or parent control
  const handleCollapse = () => {
    if (onCollapse) {
      onCollapse();
    } else {
      setIsInternallyCollapsed(prev => !prev);
    }
  };

  // If compact mode, just render the search bar
  if (compact) {
    return (
      <div className={`bg-medihub-softBg rounded-xl border border-medihub-midPurple/20 shadow-sm p-4 ${className}`}>
        <FilterSearch search={search} onSearchChange={setSearch} />
      </div>
    );
  }

  // For mobile view, return a button that opens a sheet with filters
  if (isMobile) {
    return (
      <div className={`bg-medihub-softBg rounded-xl border border-medihub-midPurple/20 shadow-sm overflow-hidden ${className}`}>
        <div className="p-4 bg-medihub-headerBg">
          <div className="flex justify-between items-center">
            <FilterSearch search={search} onSearchChange={setSearch} />
            <MobileFilterSheet
              search={search}
              setSearch={setSearch}
              location={location}
              setLocation={setLocation}
              selectedTypes={selectedTypes}
              selectedProfessions={selectedProfessions}
              hasCreditPoints={hasCreditPoints}
              dateRange={dateRange}
              currentDate={currentDate}
              handleTypeClick={handleTypeClick}
              handleProfessionClick={handleProfessionClick}
              toggleCreditPointsFilter={toggleCreditPointsFilter}
              resetFilters={resetFilters}
              setDateRange={setDateRange}
              hasActiveFilters={hasActiveFilters}
            />
          </div>

          {/* Display active filter count as a badge */}
          {hasActiveFilters && (
            <div className="mt-2">
              <div className="text-xs text-medihub-primary bg-medihub-lightPurple/30 inline-flex items-center px-2 py-1 rounded-full">
                {Object.values(selectedTypes).filter(Boolean).length +
                  Object.values(selectedProfessions).filter(Boolean).length +
                  (location ? 1 : 0) +
                  (hasCreditPoints ? 1 : 0) +
                  ((dateRange.startDate || dateRange.endDate) ? 1 : 0)} aktív szűrő
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Desktop view with collapsible panel and compact trigger
  return (
    <div className={`bg-medihub-softBg rounded-xl border border-medihub-midPurple/20 shadow-sm overflow-hidden transition-all duration-300 ${className}`}>
      <Collapsible open={!(isCollapsed || isInternallyCollapsed)} onOpenChange={handleCollapse}>
        {/* Search bar that's always visible */}
        <div className="p-4 border-b border-medihub-midPurple/20 bg-medihub-headerBg flex flex-col gap-3">
          <div className="flex-1">
            <FilterSearch search={search} onSearchChange={setSearch} />
          </div>

          <div className="flex items-center gap-2 justify-between">
            {isAuthenticated && user?.userProfile.notificationPreferences?.preferredTopics && user.userProfile.notificationPreferences.preferredTopics.length > 0 && (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="default"
                      size="sm"
                      onClick={applyPreferenceFilter}
                      className="bg-medihub-accent text-white hover:bg-medihub-accent/90 border-none shadow-md w-full sm:w-auto font-medium"
                    >
                      <Sparkles className="h-4 w-4 mr-2" />
                      <span>Nekem ajánlott</span>
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Szűrés a preferenciáid alapján</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}

            {/* Compact collapse trigger */}
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="text-medihub-primary hover:bg-medihub-lightPurple/50 h-8 w-8"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
            </CollapsibleTrigger>
          </div>
        </div>

        {/* Collapsible content */}
        <CollapsibleContent>
          <DesktopFilterContent
            location={location}
            setLocation={setLocation}
            selectedTypes={selectedTypes}
            selectedProfessions={selectedProfessions}
            hasCreditPoints={hasCreditPoints}
            dateRange={dateRange}
            currentDate={currentDate}
            search={search}
            handleTypeClick={handleTypeClick}
            handleProfessionClick={handleProfessionClick}
            toggleCreditPointsFilter={toggleCreditPointsFilter}
            resetFilters={resetFilters}
            setDateRange={setDateRange}
            hasActiveFilters={hasActiveFilters}
          />
        </CollapsibleContent>
      </Collapsible>
    </div>
  );
};

export default FilterBar;
